import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type CustomerStatus } from '../../lib/adminApi.js';
import { Icon } from '../../components/Icon.js';
import { InviteLinkBox } from '../../components/InviteLinkBox.js';
import { fmt, maskPhone } from '../../lib/format.js';

// Customer detail — status, plan/quotas, usage, team + numbers, suspend/reactivate, resend-invite.
// The monthly price + dunning grace are now REAL editable controls persisted to the Subscription
// (Stage 4 — the single source of truth for priceAgorot). Also shows subscription status, next
// charge, and the last invoice's pay-status.

const STATUS_CHIP: Record<CustomerStatus, { cls: string; label: string }> = {
  active: { cls: 'green', label: 'פעיל' },
  suspended: { cls: 'gold', label: 'מושהה' },
  cancelled: { cls: 'muted', label: 'בוטל' },
};
const ROLE_LABEL: Record<string, string> = { owner: 'בעלים', admin: 'מנהל', agent: 'סוכן' };
const SUB_STATUS_LABEL: Record<string, string> = {
  trialing: 'תקופת ניסיון', active: 'פעיל', past_due: 'באיחור תשלום', suspended: 'מושהה', cancelled: 'בוטל',
};
const INVOICE_STATUS_LABEL: Record<string, { cls: string; label: string }> = {
  draft: { cls: 'muted', label: 'טיוטה' },
  issued: { cls: 'gold', label: 'הונפקה — ממתין לתשלום' },
  paid: { cls: 'green', label: 'שולמה' },
  overdue: { cls: 'gold', label: 'באיחור' },
  failed: { cls: 'muted', label: 'נכשלה' },
  void: { cls: 'muted', label: 'בוטלה' },
};
const ilsFromAgorot = (a: number): string => `₪${(a / 100).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** LOW-5: only ever treat the hosted document URL as a link when it is a real https:// URL. Defends
 *  against a poisoned/malformed provider response (e.g. a javascript:/http: value) becoming an href. */
function safeDocHref(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

export function CustomerDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [invite, setInvite] = useState<{ url: string; expiresAt: string; smsSent: boolean } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Billing edit form (price in whole shekels for the UI; converted to agorot on save). `undefined`
  // means "not editing / show the persisted value".
  const [priceShekels, setPriceShekels] = useState<string | null>(null);
  const [graceDays, setGraceDays] = useState<string | null>(null);
  const [billingSaved, setBillingSaved] = useState(false);

  const { data: c, isLoading } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: () => adminApi.customer(id),
    enabled: Boolean(id),
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') => adminApi.updateCustomer(id, { status }),
    onSuccess: async () => {
      setConfirmSuspend(false);
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'customer', id] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
    onError: () => setActionError('העדכון נכשל. נסה שוב.'),
  });

  const resend = useMutation({
    mutationFn: (sms: boolean) => adminApi.resendInvite(id, sms),
    onSuccess: (r) => {
      setActionError(null);
      setInvite({ url: r.inviteUrl, expiresAt: r.inviteExpiresAt, smsSent: r.inviteSmsSent });
    },
    onError: () => setActionError('שליחת ההזמנה מחדש נכשלה.'),
  });

  const saveBilling = useMutation({
    mutationFn: (body: { priceAgorot?: number; graceDays?: number }) => adminApi.updateCustomer(id, body),
    onSuccess: async () => {
      setActionError(null);
      setBillingSaved(true);
      setPriceShekels(null);
      setGraceDays(null);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'customer', id] });
    },
    onError: () => setActionError('שמירת התמחור נכשלה. נסה שוב.'),
  });

  if (isLoading || !c) {
    return <div className="content"><div className="empty">טוען…</div></div>;
  }

  const chip = STATUS_CHIP[c.status];
  const owner = c.users.find((u) => u.role === 'owner');
  const ownerActivated = owner?.isActive ?? false;

  return (
    <div className="content" style={{ maxWidth: 980 }}>
      <div className="page-head">
        <div>
          <div className="eyebrow">קונסולת ניהול</div>
          <h1>{c.name}</h1>
          <div className="sub" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`chip ${chip.cls}`}>{chip.label}</span>
            {owner && <span className="ltr" style={{ color: 'var(--text-dim)' }}>{owner.email}</span>}
          </div>
        </div>
        <button className="btn ghost" onClick={() => navigate('/admin/customers')}>חזרה לרשימה</button>
      </div>

      {actionError && (
        <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 'var(--r-sm)', background: 'color-mix(in srgb, var(--risk) 12%, transparent)', color: 'var(--risk)', fontSize: 14 }}>
          {actionError}
        </div>
      )}

      <div className="grid-2">
        {/* ── usage ── */}
        <div className="card" style={{ padding: 22 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 16 }}><h3>שימוש</h3></div>
          <Row k="מספרים מחוברים" v={`${fmt(c.usage.numbers)} / ${fmt(c.maxAccounts)}`} />
          <Row k="קבוצות / יעדים" v={`עד ${fmt(c.maxTargets)}`} />
          <Row k="שידורים (30 יום)" v={fmt(c.usage.campaigns30d)} />
          <Row k="משתמשים" v={`${fmt(c.usage.users)} / ${fmt(c.seats)}`} />
        </div>

        {/* ── subscription / price (REAL — persisted to the Subscription) ── */}
        <div className="card" style={{ padding: 22 }}>
          <div className="card-h" style={{ padding: 0, marginBottom: 16 }}><h3>מנוי וחיוב</h3></div>
          <Row k="תוכנית" v={c.plan} />
          <Row k="סטטוס מנוי" v={c.subscription ? (SUB_STATUS_LABEL[c.subscription.status] ?? c.subscription.status) : '—'} />
          <Row k="חיוב הבא" v={c.subscription?.nextInvoiceAt ? new Date(c.subscription.nextInvoiceAt).toLocaleDateString('he-IL') : '—'} />

          {/* editable monthly price + grace days */}
          <div style={{ borderTop: '1px solid var(--hair)', marginTop: 12, paddingTop: 14 }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 600, display: 'block', marginBottom: 8 }} htmlFor="price">
              מחיר חודשי (₪, ללא מע״מ — עוסק פטור)
            </label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                id="price" type="number" min={0} step={1} dir="ltr"
                style={{ width: 130, height: 42, padding: '0 12px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 15 }}
                value={priceShekels ?? String(Math.round((c.subscription?.priceAgorot ?? 0) / 100))}
                onChange={(e) => { setPriceShekels(e.target.value); setBillingSaved(false); }}
              />
              <label style={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 600, marginInlineStart: 8 }} htmlFor="grace">ימי חסד</label>
              <input
                id="grace" type="number" min={0} max={90} step={1} dir="ltr"
                style={{ width: 80, height: 42, padding: '0 12px', borderRadius: 'var(--pill)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 15 }}
                value={graceDays ?? String(c.subscription?.graceDays ?? 3)}
                onChange={(e) => { setGraceDays(e.target.value); setBillingSaved(false); }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
              <button
                className="btn"
                disabled={saveBilling.isPending || (priceShekels === null && graceDays === null)}
                onClick={() => {
                  const body: { priceAgorot?: number; graceDays?: number } = {};
                  if (priceShekels !== null) body.priceAgorot = Math.max(0, Math.round(Number(priceShekels) * 100));
                  if (graceDays !== null) body.graceDays = Math.max(0, Math.min(90, Math.round(Number(graceDays))));
                  saveBilling.mutate(body);
                }}
              >
                {saveBilling.isPending ? 'שומר…' : 'שמור תמחור'}
              </button>
              {billingSaved && <span className="chip green">נשמר</span>}
            </div>
          </div>

          {c.lastInvoice && (
            <div style={{ borderTop: '1px solid var(--hair)', marginTop: 14, paddingTop: 14 }}>
              <div style={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 600, marginBottom: 8 }}>חשבונית אחרונה</div>
              <div className="gm-row" style={{ marginBottom: 8 }}>
                <span className="k">{ilsFromAgorot(c.lastInvoice.amountAgorot)}</span>
                <span className="v">
                  <span className={`chip ${INVOICE_STATUS_LABEL[c.lastInvoice.status]?.cls ?? 'muted'}`}>
                    {INVOICE_STATUS_LABEL[c.lastInvoice.status]?.label ?? c.lastInvoice.status}
                  </span>
                </span>
              </div>
              {safeDocHref(c.lastInvoice.morningDocumentUrl) && (
                <a className="ltr" href={safeDocHref(c.lastInvoice.morningDocumentUrl)!} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--brand)' }}>
                  צפייה במסמך
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── actions: suspend / reactivate + resend invite ── */}
      <div className="section-title"><h2 style={{ fontSize: 20 }}>פעולות</h2><span className="rule" /></div>
      <div className="card" style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {c.status === 'active' ? (
          !confirmSuspend ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14 }}>השהיית הלקוח עוצרת את השידורים שלו.</div>
              <button className="btn ghost" onClick={() => setConfirmSuspend(true)}>השהה לקוח</button>
            </div>
          ) : (
            <div style={{ padding: 16, borderRadius: 'var(--r-sm)', background: 'color-mix(in srgb, var(--risk) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--risk) 30%, transparent)' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>להשהות את הלקוח?</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-dim)', marginBottom: 14 }}>לקוח מושהה לא יוכל לשדר הודעות עד להחזרתו לפעילות.</div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn" style={{ background: 'var(--risk)' }} disabled={setStatus.isPending} onClick={() => setStatus.mutate('suspended')}>
                  {setStatus.isPending ? 'משהה…' : 'כן, השהה'}
                </button>
                <button className="btn ghost" onClick={() => setConfirmSuspend(false)}>ביטול</button>
              </div>
            </div>
          )
        ) : c.status === 'suspended' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14 }}>הלקוח מושהה. החזרה לפעילות מאפשרת לו לשדר שוב.</div>
            <button className="btn" disabled={setStatus.isPending} onClick={() => setStatus.mutate('active')}>
              {setStatus.isPending ? 'מחזיר…' : 'החזר לפעילות'}
            </button>
          </div>
        ) : null}

        {owner && !ownerActivated && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderTop: '1px solid var(--hair)', paddingTop: 16 }}>
            <div style={{ fontSize: 14 }}>הבעלים עדיין לא הפעיל את החשבון.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn ghost" disabled={resend.isPending} onClick={() => resend.mutate(false)}>
                {resend.isPending ? 'מנפיק…' : 'שלח הזמנה מחדש'}
              </button>
              <button className="btn ghost" disabled={resend.isPending} onClick={() => resend.mutate(true)}>
                <Icon name="phone" /> הזמנה מחדש + SMS
              </button>
            </div>
          </div>
        )}
      </div>

      {invite && (
        <div style={{ marginTop: 16 }}>
          <InviteLinkBox url={invite.url} expiresAt={invite.expiresAt} smsSent={invite.smsSent} />
        </div>
      )}

      {/* ── team ── */}
      <div className="section-title"><h2 style={{ fontSize: 20 }}>צוות</h2><span className="rule" /></div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="dtable">
          <thead><tr><th>אימייל</th><th>שם</th><th>תפקיד</th><th>סטטוס</th></tr></thead>
          <tbody>
            {c.users.map((u) => (
              <tr key={u.id}>
                <td className="ltr" style={{ fontWeight: 600 }}>{u.email}</td>
                <td>{u.name ?? '—'}</td>
                <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                <td><span className={`chip ${u.isActive ? 'green' : 'gold'}`}>{u.isActive ? 'פעיל' : 'טרם הופעל'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── numbers ── */}
      {c.waAccounts.length > 0 && (
        <>
          <div className="section-title"><h2 style={{ fontSize: 20 }}>מספרים</h2><span className="rule" /></div>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="dtable">
              <thead><tr><th>תווית</th><th>מספר</th><th>מצב</th></tr></thead>
              <tbody>
                {c.waAccounts.map((w) => (
                  <tr key={w.id}>
                    <td style={{ fontWeight: 600 }}>{w.label}</td>
                    <td className="mono ltr">{maskPhone(w.phoneNumber)}</td>
                    <td style={{ color: 'var(--text-dim)', fontSize: 13 }}>{w.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="gm-row" style={{ marginBottom: 10 }}>
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}
